<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            :ajax-params="serversParams"
            search-placeholder="Search Servers or Accounts"
            ajax-path="cpanel/servers"
            @searchDone="handleSearchServersDone"
        />
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Server</template>
            <template slot="body">
                <app-input
                    v-model="addForm.name"
                    placeholder="Host Name"
                    :error="addForm.errors.get('name')"
                    @updated="addForm.errors.clear('name')"
                    focus
                    required
                />
                <app-input
                    v-model="addForm.username"
                    placeholder="Username"
                    :error="addForm.errors.get('username')"
                    @updated="addForm.errors.clear('username')"
                    required
                />
                <app-input
                    type="password"
                    v-model="addForm.password"
                    placeholder="Password"
                    :error="addForm.errors.get('password')"
                    @updated="addForm.errors.clear('password')"
                    required
                />
                <app-input
                    type="password"
                    v-model="addForm.api_token"
                    placeholder="API Token"
                    :error="addForm.errors.get('api_token')"
                    @updated="addForm.errors.clear('api_token')"
                />
                <app-input
                    v-model="addForm.description"
                    placeholder="Description"
                    :error="addForm.errors.get('description')"
                    @updated="addForm.errors.clear('description')"
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/4"
            @close="editModalClosed"
        >
            <template slot="header">Edit Server</template>
            <template slot="body">                
                <app-input
                    type="password"
                    v-model="editForm.api_token"
                    placeholder="API Token"
                    :error="editForm.errors.get('api_token')"
                    @updated="editForm.errors.clear('api_token')"
                />
                <app-input
                    v-model="editForm.description"
                    placeholder="Description"
                    :error="editForm.errors.get('description')"
                    @updated="editForm.errors.clear('description')"
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAccountsModal"
            width="lg:w-3/4"
            custom-class="flex flex-col h-full"
        >
            <template slot="header" v-if="selected.name">{{ selected.name }} Accounts</template>
            <template slot="header" v-else>Accounts Results for "{{ q }}"</template>
            <template slot="body">
                <tabulator
                    :columns="[
                        {
                            title: 'Domain',
                            field: 'domain',
                            sorter: 'string',
                            minWidth: 250,
                        },
                        {
                            title: 'User',
                            field: 'user',
                            sorter: 'string',
                            minWidth: 150,
                        },
                        {
                            title: 'Server',
                            field: 'server.name',
                            sorter: 'string',
                            minWidth: 250,
                        },
                        {
                            title: 'Status',
                            field: 'suspended',
                            sorter: 'string',
                            minWidth: 130,
                            width: 130,
                            widthGrow: false,
                            formatter(cell, params) {
                                if (cell._cell.value) {
                                    return `<span class='badge badge-red'>Suspended</span>`;
                                }

                                return `<span class='badge badge-green'>Active</span>`;
                            }
                        },
                    ]"
                    :actions="domainActions"
                    ajax-path="cpanel/domains"
                    :refresh.sync="refreshDomainsTable"
                    :ajax-params="domainsParams"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddonDomainsModal"
            width="lg:w-3/4"
            custom-class="flex flex-col h-full"
        >
            <template slot="header" v-if="selectedDomain.domain">{{ selectedDomain.domain }} Addon Domains</template>
            <template slot="header" v-else>Addon Domain Results for "{{ q }}"</template>
            <template slot="body">
                <tabulator
                    :columns="[
                        {
                            title: 'Domain',
                            field: 'domain',
                            sorter: 'string',
                            minWidth: 250,
                        },
                        {
                            title: 'User',
                            field: 'parent_domain.user',
                            sorter: 'string',
                            minWidth: 150,
                        },
                        {
                            title: 'Server',
                            field: 'parent_domain.server.name',
                            sorter: 'string',
                            minWidth: 250,
                        },
                        {
                            title: 'Status',
                            field: 'status',
                            sorter: 'string',
                            minWidth: 130,
                            width: 130,
                            widthGrow: false,
                        },
                    ]"
                    :actions="domainActions"
                    ajax-path="cpanel/addon-domains"
                    :refresh.sync="refreshAddonDomainsTable"
                    :ajax-params="addonDomainsParams"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEmailAccountsModal"
            width="lg:w-3/4"
            custom-class="flex flex-col h-full"
        >
            <template slot="header">{{ selectedDomain.domain }} Email Accounts</template>
            <template slot="body">
                <tabulator
                    :columns="[
                        {
                            title: 'Email',
                            field: 'email',
                            sorter: 'string',
                            minWidth: 250,
                        },
                        {
                            title: 'Usage',
                            sorter: 'string',
                            minWidth: 250,
                            formatter({_cell}) {
                                return `${_cell.row.data.humandiskused == 'None' ? '0 MB' : _cell.row.data.humandiskused} (${_cell.row.data.diskusedpercent}%) of ${_cell.row.data.humandiskquota == 'None' ? 'Unlimited' : _cell.row.data.humandiskquota}`;
                            }
                        },
                        {
                            title: 'Incoming',
                            field: 'suspended_incoming',
                            sorter: 'string',
                            minWidth: 150,
                            formatter({_cell}) {
                                if (_cell.value) {
                                    return `<span class='badge badge-red'>Suspended</span>`;
                                }

                                return `<span class='badge badge-green'>Unsuspended</span>`;
                            }
                        },
                        {
                            title: 'Login',
                            field: 'suspended_login',
                            sorter: 'string',
                            minWidth: 150,
                            formatter({_cell}) {
                                if (_cell.value) {
                                    return `<span class='badge badge-red'>Suspended</span>`;
                                }

                                return `<span class='badge badge-green'>Unsuspended</span>`;
                            }
                        }                        
                    ]"
                    ajax-path="cpanel/emails"
                    :refresh.sync="refreshEmailAccountsTable"
                    :ajax-params="emailAccountsParams"
                    :actions="emailAccountsActions"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showLoginModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Login</template>
            <template slot="body">
                <div 
                    v-if="generatingLoggedInUrl"
                    class="flex items-center justify-center"
                >
                    <app-spinner class="mr-2 text-gray-500" />
                    <div>Generating url...</div>
                </div>
                <div v-else>
                    <div class="mb-4">Click the button to login</div>
                    <a 
                        class="btn btn-block btn-blue text-white" 
                        :href="loggedInUrl" 
                        target="_blank"
                    >Login</a>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showChangeAccountPasswordModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Change Password</template>
            <template slot="body">
                <app-input
                    v-model="changeAccountPasswordForm.domain"
                    placeholder="Domain"
                    disable
                />
                <app-input
                    type="text"
                    v-model="changeAccountPasswordForm.password"
                    placeholder="New Password"
                    :error="changeAccountPasswordForm.errors.get('password')"
                    @updated="changeAccountPasswordForm.errors.clear('password')"
                    required
                    focus
                    :loading="resetAccountPasswordFormGeneratingPassword"
                    :action="{
                        icon: () => import('@/icons/Refresh.vue'),
                        label: 'Regenerate Password',
                        handler: generatePasswordForResetAccountPassword
                    }"
                />                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showChangeAccountPasswordModal = false"
                        :disable="changingAccountPassword"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="updateAccountPassword"
                        :loading="changingAccountPassword"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showChangeServerPasswordModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Change Password</template>
            <template slot="body">
                <app-input
                    v-model="changeServerPasswordForm.name"
                    placeholder="Server"
                    disable
                />
                <app-input
                    type="password"
                    v-model="changeServerPasswordForm.password"
                    placeholder="New Password"
                    required
                    focus
                    :error="changeServerPasswordForm.errors.get('password')"
                    @updated="changeServerPasswordForm.errors.clear('password')"
                />
                <app-input
                    type="password"
                    v-model="changeServerPasswordForm.password_confirmation"
                    placeholder="Confirm New Password"
                    required
                    :error="changeServerPasswordForm.errors.get('password_confirmation')"
                    @updated="changeServerPasswordForm.errors.clear('password_confirmation')"
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showChangeServerPasswordModal = false"
                        :disable="changingServerPassword"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="updateServerPassword"
                        :loading="changingServerPassword"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showBlockIpModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Block IP for {{ selected.name }}</template>
            <template slot="body">
                <app-input
                    v-model="blockIpForm.ip"
                    placeholder="IP"
                    :error="blockIpForm.errors.get('ip')"
                    @updated="blockIpForm.errors.clear('ip')"
                    focus
                    required
                />                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showBlockIpModal = false"
                        :disable="blockingIp"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="blockIp"
                        :loading="blockingIp"
                    >Block</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showUnblockIpModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Unblock IP for {{ selected.name }}</template>
            <template slot="body">
                <app-input
                    v-model="unblockIpForm.ip"
                    placeholder="IP"
                    :error="unblockIpForm.errors.get('ip')"
                    @updated="unblockIpForm.errors.clear('ip')"
                    focus
                    required
                />                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showUnblockIpModal = false"
                        :disable="unblockingIp"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="unblockIp"
                        :loading="unblockingIp"
                    >Unblock</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showWhitelistIpModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Whitelist IP for {{ selected.name }}</template>
            <template slot="body">
                <app-input
                    v-model="whitelistIpForm.ip"
                    placeholder="IP"
                    :error="whitelistIpForm.errors.get('ip')"
                    @updated="whitelistIpForm.errors.clear('ip')"
                    focus
                    required
                />                
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showWhitelistIpModal = false"
                        :disable="whitelistingIp"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="whitelistIp"
                        :loading="whitelistingIp"
                    >Whitelist</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showResetEmailPasswordModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Reset Email Password</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="resetEmailPasswordForm.email"
                    placeholder="Email"
                    :error="resetEmailPasswordForm.errors.get('email')"
                    @updated="resetEmailPasswordForm.errors.clear('email')"
                    required
                    disable
                />
                <app-input
                    type="text"
                    v-model="resetEmailPasswordForm.password"
                    placeholder="New Password"
                    :error="resetEmailPasswordForm.errors.get('password')"
                    @updated="resetEmailPasswordForm.errors.clear('password')"
                    required
                    :loading="resetEmailPasswordFormGeneratingPassword"
                    :action="{
                        icon: () => import('@/icons/Refresh.vue'),
                        label: 'Regenerate Password',
                        handler: generatePasswordForResetEmailPassword
                    }"
                />
                <app-input 
                    type="text"
                    v-model="resetEmailPasswordForm.mobile_number"
                    placeholder="Mobile Number"
                    :error="resetEmailPasswordForm.errors.get('mobile_number')"
                    @updated="resetEmailPasswordForm.errors.clear('mobile_number')"
                    help-text="The new password will be sent to this number."
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showResetEmailPasswordModal = false"
                        :disable="resettingEmailPassword"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="resetEmailPassword"
                        :loading="resettingEmailPassword"
                    >Reset Password</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditEmailQuotaModal"
            width="md:w-1/2 lg:w-1/4"
        >
            <template slot="header">Edit Email Quota</template>
            <template slot="body">
                <app-input
                    type="text"
                    v-model="editEmailQuotaForm.email"
                    placeholder="Email"
                    :error="editEmailQuotaForm.errors.get('email')"
                    @updated="editEmailQuotaForm.errors.clear('email')"
                    required
                    disable
                />                
                <app-input 
                    type="text"
                    v-model="editEmailQuotaForm.quota"
                    placeholder="Quota"
                    :error="editEmailQuotaForm.errors.get('quota')"
                    @updated="editEmailQuotaForm.errors.clear('quota')"
                    help-text="To grant unlimited disk space, specify 0."
                    required
                    append-text="MB"
                    float
                />
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditEmailQuotaModal = false"
                        :disable="updatingEmailQuota"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="updateEmailQuota"
                        :loading="updatingEmailQuota"
                    >Update Quota</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    
    export default {
        data() {
            return {
                q: null,
                showLoginModal: false,
                showAccountsModal: false,
                showBlockIpModal: false,
                showUnblockIpModal: false,
                showWhitelistIpModal: false,
                showAddModal: false,
                showEditModal: false,
                showChangeAccountPasswordModal: false,
                showChangeServerPasswordModal: false,
                showAddonDomainsModal: false,
                showEmailAccountsModal: false,
                showResetEmailPasswordModal: false,
                showEditEmailQuotaModal: false,
                changingAccountPassword: false,
                changingServerPassword: false,
                resettingEmailPassword: false,
                updatingEmailQuota: false,
                resetEmailPasswordFormGeneratingPassword: false,
                resetAccountPasswordFormGeneratingPassword: false,
                addFormIsLoading: false,
                editFormIsLoading: false,
                tableIsLoading: false,
                tableLoadingText: null,
                refreshTable: false,
                refreshDomainsTable: false,
                refreshAddonDomainsTable: false,
                refreshEmailAccountsTable: false,
                domainsParams: {},
                serversParams: {},
                addonDomainsParams: {},
                emailAccountsParams: {},
                selected: {},
                selectedDomain: {},
                generatingLoggedInUrl: false,
                loggedInUrl: null,
                blockingIp: false,
                unblockingIp: false,
                whitelistingIp: false,
                whitelistIpForm: new Form({
                    ip: null,
                }),
                blockIpForm: new Form({
                    ip: null,
                }),
                unblockIpForm: new Form({
                    ip: null,
                }),
                editEmailQuotaForm: new Form({
                    email: null,
                    quota: null,
                }),
                changeAccountPasswordForm: new Form({
                    domain: null,
                    password: null,
                }),
                changeServerPasswordForm: new Form({
                    name: null,
                    password: null,
                    password_confirmation: null,
                }),
                addForm: new Form({
                    name: null,
                    username: null,
                    password: null,
                    api_token: null,
                    description: null,
                }),
                editForm: new Form({
                    id: null,
                    api_token: null,
                    description: null,
                }),
                resetEmailPasswordForm: new Form({
                    email: null,
                    password: null,
                    mobile_number: null,
                }),
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Description',
                        field: 'description',
                        sorter: 'string',
                        minWidth: 200,
                        formatter({_cell}) {
                            return _cell.value || '--';
                        }
                    },
                    {
                        title: 'Username',
                        field: 'username',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Password',
                        field: 'password',
                        sorter: 'string',
                        minWidth: 130,
                        formatter() {
                            return '*********';
                        }
                    },
                ],
                actions: [
                    {
                        label: 'Accounts',
                        icon: () => import('@/icons/PeopleOutlined.vue'),
                        handler: (data) => {
                            this.domainsParams = {
                                server_id: data[0].id,
                            }
                            this.selected = data[0];
                            this.showAccountsModal = true;
                        },
                    },
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: (data) => {
                            this.selected = data[0];
                            this.editForm.set(data[0]);
                            this.showEditModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: (data) => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to delete "<strong>${data[0].name}</strong>".`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, delete it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.delete(`cpanel/servers/${data[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Server was successfully deleted.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Login',
                        icon: () => import('@/icons/VpnKeyOutlined.vue'),
                        handler: (data) => {
                            this.showLoginModal = true;
                            this.generatingLoggedInUrl = true;
                            Http.post('cpanel/servers/login', {
                                host: data[0].name,
                                username: data[0].username,
                                password: data[0].password
                            })
                            .then(({ data }) => {
                                this.loggedInUrl = data.url;
                                this.generatingLoggedInUrl = false;
                            })
                            .catch(({ response }) => {
                                this.generatingLoggedInUrl = false;
                                this.showLoginModal = false;
                            })
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddModal = true;
                        },
                    },
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    },
                    {
                        label: 'Change Password',
                        icon: () => import('@/icons/LockOutlined.vue'),
                        handler: items => {
                            this.selected = items[0];
                            this.showChangeServerPasswordModal = true;
                            this.changeServerPasswordForm.reset();
                            this.changeServerPasswordForm.errors.clear();
                            this.changeServerPasswordForm.set({name: items[0].name});
                        }
                    },
                    {
                        label: 'Whitelist IP',
                        icon: () => import('@/icons/ListAltOutlined.vue'),
                        handler: items => {
                            this.selected = items[0];
                            this.showWhitelistIpModal = true;
                            this.whitelistIpForm.reset();
                            this.whitelistIpForm.errors.clear();
                        }
                    },
                    {
                        label: 'Block IP',
                        icon: () => import('@/icons/BlockOutlined.vue'),
                        handler: items => {
                            this.selected = items[0];
                            this.showBlockIpModal = true;
                            this.blockIpForm.reset();
                            this.blockIpForm.errors.clear();
                        }
                    },
                    {
                        label: 'Unblock IP',
                        icon: () => import('@/icons/LockOpenOutlined.vue'),
                        handler: items => {
                            this.selected = items[0];
                            this.showUnblockIpModal = true;
                            this.unblockIpForm.reset();
                            this.unblockIpForm.errors.clear();
                        }
                    },
                ],
                domainActions: [
                    {
                        label: 'Login',
                        icon: () => import('@/icons/VpnKeyOutlined.vue'),
                        handler: (data) => {
                            let server = data[0].server || data[0].domain.server;
                            let domain = data[0].server_id ? data[0] : data[0].domain;

                            this.showLoginModal = true;
                            this.generatingLoggedInUrl = true;
                            Http.post('cpanel/domains/login', {
                                host: server.name,
                                domain_username: domain.user,
                                username: server.username,
                                password: server.password
                            })
                            .then(({ data }) => {
                                this.loggedInUrl = data.url;
                                this.generatingLoggedInUrl = false;
                            })
                            .catch(({ response }) => {
                                this.generatingLoggedInUrl = false;
                                this.showLoginModal = false;
                            })
                        }
                    },
                    {
                        label: 'Email Accounts',
                        icon: () => import('@/icons/EmailAlternate.vue'),
                        isEligible(items) {
                            return items[0].server_id;
                        },
                        handler: items => {
                            this.showEmailAccountsModal = true;
                            this.selectedDomain = items[0];
                            this.emailAccountsParams = {
                                domain_id: items[0].id,
                            }
                        }
                    },
                    {
                        label: 'Addon Domains',
                        icon: () => import('@/icons/ExtensionOutlined.vue'),
                        isEligible(items) {
                            return items[0].server_id;
                        },
                        handler: items => {
                            this.showAddonDomainsModal = true;
                            this.selectedDomain = items[0];
                            this.addonDomainsParams = {
                                domain_id: items[0].id, 
                            }
                        }
                    },
                    {
                        label: 'Unsuspend',
                        icon: () => import('@/icons/CheckCircle.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;

                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to unsuspend "<strong>${domain.domain}</strong>".`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, unsuspend it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`cpanel/domains/${domain.id}/unsuspend`)
                                               .catch(({ response }) => {
                                                    this.$swal.showValidationMessage(response.message);
                                               });
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshDomainsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Account was successfully unsuspended.'
                                    })
                                }
                            })
                        },
                        isEligible(items) {
                            return items[0].suspended;
                        }
                    },
                    {
                        label: 'Suspend',
                        icon: () => import('@/icons/BlockOutlined.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to suspend "<strong>${items[0].domain}</strong>". Please enter a reason below.`,
                                input: 'text',
                                inputPlaceholder: 'Reason',
                                inputValue: 'No longer hosted',
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, suspend it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => { console.log(response)
                                    return Http.patch(`cpanel/domains/${items[0].id}/suspend`)
                                               .catch(({ response }) => {
                                                    this.$swal.showValidationMessage(response.message);
                                               });
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshDomainsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Account was successfully suspended.'
                                    })
                                }
                            })
                        },
                        isEligible(items) {
                            return !items[0].suspended;
                        }
                    },
                    {
                        label: 'Change Password',
                        icon: () => import('@/icons/LockOutlined.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            this.selected = domain;
                            this.showChangeAccountPasswordModal = true;
                            this.generatePasswordForResetAccountPassword();
                            this.changeAccountPasswordForm.reset();
                            this.changeAccountPasswordForm.errors.clear();
                            this.changeAccountPasswordForm.set(domain);
                        }
                    },
                    {
                        label: 'Login to Server',
                        icon: () => import('@/icons/Storage.vue'),
                        handler: (items) => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            let server = domain.server;
                            this.showLoginModal = true;
                            this.generatingLoggedInUrl = true;
                            Http.post('cpanel/servers/login', {
                                host: server.name,
                                username: server.username,
                                password: server.password
                            })
                            .then(({ data }) => {
                                this.loggedInUrl = data.url;
                                this.generatingLoggedInUrl = false;
                            })
                            .catch(({ response }) => {
                                this.generatingLoggedInUrl = false;
                                this.showLoginModal = false;
                            })
                        }
                    },
                    {
                        label: 'Whitelist IP',
                        icon: () => import('@/icons/ListAltOutlined.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            this.selected = domain.server;
                            this.showWhitelistIpModal = true;
                            this.whitelistIpForm.reset();
                            this.whitelistIpForm.errors.clear();
                        }
                    },
                    {
                        label: 'Block IP',
                        icon: () => import('@/icons/BlockOutlined.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            this.selected = domain.server;
                            this.showBlockIpModal = true;
                            this.blockIpForm.reset();
                            this.blockIpForm.errors.clear();
                        }
                    },
                    {
                        label: 'Unblock IP',
                        icon: () => import('@/icons/LockOpenOutlined.vue'),
                        handler: items => {
                            let domain = items[0].server_id ? items[0] : items[0].domain;
                            this.selected = domain.server;
                            this.showUnblockIpModal = true;
                            this.unblockIpForm.reset();
                            this.unblockIpForm.errors.clear();
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: (data) => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to delete "<strong>${data[0].domain}</strong>" account.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, delete it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.delete(`cpanel/domains/${data[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Account was successfully deleted.'
                                    })
                                }
                            })
                        }
                    },
                ],
                emailAccountsActions: [
                    {
                        label: 'Reset Password',
                        icon: () => import('@/icons/LockOutlined.vue'),
                        handler: items => {
                            this.generatePasswordForResetEmailPassword();
                            this.showResetEmailPasswordModal = true;
                            this.resetEmailPasswordForm.set({
                                email: items[0].email
                            });
                        }
                    },
                    {
                        label: 'Edit Quota',
                        icon: () => import('@/icons/PieChartOutlined.vue'),
                        handler: items => {
                            this.showEditEmailQuotaModal = true;
                            this.editEmailQuotaForm.set({
                                email: items[0].email,
                                quota: items[0].diskquota == 'unlimited' ? 0 : items[0].diskquota,
                            });
                        }
                    },
                    {
                        label: 'Suspend Incoming',
                        icon: () => import('@/icons/NotificationsOffOutlined.vue'),
                        isEligible(items) {
                            return !items[0].suspended_incoming
                        },
                        handler: items => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to suspend "<strong>${items[0].email}</strong>" the ability to recieve incoming messages.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, suspend it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`cpanel/emails/suspend_incoming/${items[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshEmailAccountsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Incoming messages was successfully suspended.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Unsuspend Incoming',
                        icon: () => import('@/icons/NotificationsActiveOutlined.vue'),
                        isEligible(items) {
                            return items[0].suspended_incoming
                        },
                        handler: items => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to activate "<strong>${items[0].email}</strong>" the ability to recieve incoming messages.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, activate it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`cpanel/emails/unsuspend_incoming/${items[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshEmailAccountsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Incoming messages was successfully activated.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Suspend Login',
                        icon: () => import('@/icons/WorkOffOutlined.vue'),
                        isEligible(items) {
                            return !items[0].suspended_login
                        },
                        handler: items => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to suspend "<strong>${items[0].email}</strong>" the ability to login.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, suspend it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`cpanel/emails/suspend_login/${items[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshEmailAccountsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Logging in was successfully suspended.'
                                    })
                                }
                            })
                        }
                    },
                    {
                        label: 'Unsuspend Login',
                        icon: () => import('@/icons/NotificationsActiveOutlined.vue'),
                        isEligible(items) {
                            return items[0].suspended_login
                        },
                        handler: items => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to activate "<strong>${items[0].email}</strong>" the ability to login.`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, activate it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.patch(`cpanel/emails/unsuspend_login/${items[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshEmailAccountsTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Logging in was successfully activated.'
                                    })
                                }
                            })
                        }
                    },
                ],
            }
        },
        methods: {
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();
            },
            store() {
                this.addFormIsLoading = true;
                this.addForm.errors.clear();
                Http.post('cpanel/servers', this.addForm.payload())
                    .then(({ data }) => {
                        this.addFormIsLoading = false;
                        this.refreshTable = true;
                        this.showAddModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Server was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            update() {
                this.editFormIsLoading = true;
                this.editForm.errors.clear();
                Http.patch(`cpanel/servers/${this.selected.id}`, this.editForm.payload())
                    .then(({ data }) => {
                        this.editFormIsLoading = false;
                        this.refreshTable = true;
                        this.showEditModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Server was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    })
            },
            updateAccountPassword() {
                this.changingAccountPassword = true;
                Http.patch(`cpanel/domains/${this.selected.id}/password`, this.changeAccountPasswordForm.payload())
                    .then(({data}) => {
                        this.changingAccountPassword = false;
                        this.showChangeAccountPasswordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Account password was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.changingAccountPassword = false;
                        this.changeAccountPasswordForm.errors.record(response.data);
                    });
            },
            updateServerPassword() {
                this.changingServerPassword = true;
                Http.patch(`cpanel/servers/${this.selected.id}/password`, this.changeServerPasswordForm.payload())
                    .then(({data}) => {
                        this.changingServerPassword = false;
                        this.showChangeServerPasswordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Server password was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.changingServerPassword = false;
                        this.changeServerPasswordForm.errors.record(response.data);
                    });
            },
            blockIp() {
                this.blockingIp = true;
                Http.post(`cpanel/servers/${this.selected.id}/blockip`, this.blockIpForm.payload())
                    .then(() => {
                        this.blockingIp = false;
                        this.showBlockIpModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: `${this.blockIpForm.ip} is now blocked.`
                        });
                    })
                    .catch(({ response }) => {
                        this.blockingIp = false;
                        this.blockIpForm.errors.record(response.data);
                    })
            },
            unblockIp() {
                this.unblockingIp = true;
                Http.post(`cpanel/servers/${this.selected.id}/unblockip`, this.unblockIpForm.payload())
                    .then(() => {
                        this.unblockingIp = false;
                        this.showUnblockIpModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: `${this.unblockIpForm.ip} is now unblocked.`
                        });
                    })
                    .catch(({ response }) => {
                        this.unblockingIp = false;
                        this.unblockIpForm.errors.record(response.data);
                    })
            },
            whitelistIp() {
                this.whitelistingIp = true;
                Http.post(`cpanel/servers/${this.selected.id}/whitelistip`, this.whitelistIpForm.payload())
                    .then(() => {
                        this.whitelistingIp = false;
                        this.showWhitelistIpModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: `${this.whitelistIpForm.ip} is now whitelisted.`
                        });
                    })
                    .catch(({ response }) => {
                        this.whitelistingIp = false;
                        this.whitelistIpForm.errors.record(response.data);
                    })
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                Http.patch('cpanel/servers/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            handleSearchServersDone(payload) {
                if (payload.response.data.length === 0) {
                    this.selected = {};
                    this.tableIsLoading = true;
                    this.tableLoadingText = 'Searching accounts...';
                    Http.get('cpanel/domains', {params: {q: payload.q}})
                        .then(({ data }) => {                            
                            if (data.data.length) {
                                this.tableIsLoading = false;
                                this.serversParams = {q: null};
                                this.domainsParams = {
                                    q: payload.q
                                }
                                this.showAccountsModal = true;
                                this.q = payload.q;
                            } else {
                                Http.get('cpanel/addon-domains', {params: {q: payload.q}})
                                    .then(({ data }) => {
                                        this.tableIsLoading = false;
                                        if (data.data.length) {
                                            this.serversParams = {q: null};
                                            this.addonDomainsParams = {
                                                q: payload.q
                                            }
                                            this.showAddonDomainsModal = true;
                                            this.q = payload.q;
                                        }
                                    })
                                    .catch(() => {
                                        this.tableIsLoading = false;
                                    })
                            }
                        })
                        .catch(() => {
                            this.tableIsLoading = false;
                        })
                }
            },
            generatePasswordForResetEmailPassword() {
                this.resetEmailPasswordFormGeneratingPassword = true;
                Http.get('password/strong')
                    .then(({data}) => {
                        this.resetEmailPasswordFormGeneratingPassword = false;
                        this.resetEmailPasswordForm.set({
                            password: data.password
                        });
                    });
            },
            generatePasswordForResetAccountPassword() {
                this.resetAccountPasswordFormGeneratingPassword = true;
                Http.get('password/strong')
                    .then(({data}) => {
                        this.resetAccountPasswordFormGeneratingPassword = false;
                        this.changeAccountPasswordForm.set({
                            password: data.password,
                        });
                    });
            },
            resetEmailPassword() {
                this.resettingEmailPassword = true;
                Http.patch('cpanel/emails/password', this.resetEmailPasswordForm.payload())
                    .then(({ data }) => {
                        this.resettingEmailPassword = false;
                        this.showResetPasswordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Password was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.resettingEmailPassword = false;
                        this.resetEmailPasswordForm.errors.record(response.data);
                    })
            },
            updateEmailQuota() {
                this.updatingEmailQuota = true;
                Http.patch('cpanel/emails/quota', this.editEmailQuotaForm.payload())
                    .then(({ data }) => {
                        this.updatingEmailQuota = false;
                        this.showEditEmailQuotaModal = false;
                        this.refreshEmailAccountsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Quota was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.updatingEmailQuota = false;
                        this.editEmailQuotaForm.errors.record(response.data);
                    })
            }
        }
    }
</script>